// AJAX Filtering for Content List
// Exposes a FilteringModule factory that holds state and all core methods.
// To create a variant, call FilteringModule({ ...overrides }) and then .init().
//
// Example override:
//   const myFilter = FilteringModule({
//       selectors: { form: '#my-form', results: '#my-results' },
//       renderError(container) {
//           container.innerHTML = '<p class="my-error">Something went wrong.</p>';
//       }
//   });
//   myFilter.init();

/**
 * Factory function that creates a filtering module instance.
 * @param {Object} options - Overrides for selectors or any public method.
 * @returns {Object} Public API: { init, bindEvents, fetchResults, getQueryString, removePaginationInputs, resolveAjaxUrl, renderError, showLoading, hideLoading, updateClearButton, state }
 */
function FilteringModule(options = {}) {

    // --- Filter Params ---
    // The URL query param names that indicate pre-filtering is active on page load.
    // Add to this list (or override via options.filterParams) as new filter types are introduced.
    const filterParams = options.filterParams || [
        'insighttypes',
    ];

    // --- Selectors ---
    // Override any selector string to target different elements on a variant page.
    const selectors = Object.assign({
        form:           '#filter-form',
        filterSelects:  '.filter-select',
        keyword:        '#keyword',
        clearButton:    '#clear-filters',
        results:        '#results-container',
        loadingOverlay: '#loading-overlay',
        ajaxUrlSource:  '.filtered-content-ajax',
        counter:        '#results-counter',
    }, options.selectors);

    // --- State ---
    // Shared mutable state for the module instance.
    const state = {
        ajaxUrl:    null,
        form:       null,
        results:    null,
        overlay:    null,
        keyword:    null,
        clearBtn:   null,
        selects:    [],
    };

    // --- Core Methods ---
    // Each method can be replaced via options for a variant.

    /**
     * Resolve and normalize the base AJAX URL from the page element.
     * @returns {string|null}
     */
    function resolveAjaxUrl() {
        const el = document.querySelector(selectors.ajaxUrlSource);
        if (!el) return null;
        let url = el.dataset.ajaxUrl || '';
        url = url.replace(/-page-\d+-?/gi, '');
        url = url.replace(/\/page\/\d+\/?/gi, '');
        url = url.replace(/\/$/, '');
        return url;
    }

    /**
     * Build the query string from the current form state.
     * @param {boolean} excludePagination
     * @returns {string}
     */
    function getQueryString(excludePagination = false) {
        const formData = new FormData(state.form);
        const params = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
            if (!value) continue;
            if (excludePagination && (key === 'page' || key === 'PageNumber' || key === 'currentPage')) {
                continue;
            }
            params.append(key, value);
        }

        return params.toString();
    }

    /**
     * Strip any pagination hidden inputs from the form.
     */
    function removePaginationInputs() {
        state.form
            .querySelectorAll('input[name="page"], input[name="PageNumber"], input[name="currentPage"]')
            .forEach(input => input.remove());
    }

    /**
     * Render an error message into the results container.
     * Override this to use custom markup or a toast notification.
     * @param {Element} container
     * @param {Error} error
     */
    function renderError(container, error) {
        container.innerHTML = '<div class="rounded-lg bg-red-50 p-8 text-center"><p class="text-red-600">Error loading results. Please try again.</p></div>';
    }

    /**
     * Show the loading state.
     */
    function showLoading() {
        state.overlay.classList.remove('hidden');
        state.results.style.opacity = '0.5';
    }

    /**
     * Hide the loading state.
     */
    function hideLoading() {
        state.overlay.classList.add('hidden');
        state.results.style.opacity = '1';
    }

    /**
     * Update the visibility of the clear-filters button based on active values.
     */
    function updateClearButton() {
        if (!state.clearBtn) return;
        const hasFilters = Array.from(new FormData(state.form).values()).some(v => v);
        state.clearBtn.style.display = hasFilters ? 'block' : 'none';
    }

    /**
     * Fetch and render filtered results via AJAX.
     * @param {boolean} resetPagination
     */
    async function fetchResults(resetPagination = false) {
        const queryString = module.getQueryString(resetPagination);
        const qs = queryString ? `${queryString}&isAjax=1` : 'isAjax=1';
        const url = `${state.ajaxUrl}?${qs}`;

        module.showLoading();

        try {
            const response = await fetch(url, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();

            if (!html || html.trim().length === 0) {
                throw new Error('Empty response from server');
            }

            // The server returns the full page HTML. Parse and extract only the
            // results container so the outerHTML swap targets the right element.
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fragment = doc.querySelector(selectors.results);

            if (!fragment) {
                throw new Error('Results container not found in response');
            }

            state.results.outerHTML = fragment.outerHTML;

            // Re-query after outerHTML replacement, which creates a new DOM node.
            state.results = document.querySelector(selectors.results);

            if (!state.results) {
                throw new Error('Results container lost after outerHTML swap');
            }
            // Strip the internal ajax flag before pushing to browser history
            const historyParams = new URLSearchParams(queryString);
            historyParams.delete('isAjax');
            const historyUrl = historyParams.toString()
                ? `${state.ajaxUrl}?${historyParams.toString()}`
                : state.ajaxUrl;
            window.history.pushState({}, '', historyUrl);
            module.updateClearButton();
            module.updateCounter();

        } catch (error) {
            if (window.location.hostname === 'localhost') {
                console.error('Error fetching results:', error);
            }
            module.renderError(state.results, error);
        } finally {
            module.hideLoading();
        }
    }

    /**
     * Bind all event listeners to the DOM elements in state.
     */
    function bindEvents() {
        state.selects.forEach(select => {
            select.addEventListener('change', function () {
                module.removePaginationInputs();
                module.fetchResults(true);
            });
        });

        const submitBtn = document.querySelector('#submit-filters');
        if (submitBtn) {
            submitBtn.addEventListener('click', function () {
                module.removePaginationInputs();
                module.fetchResults(true);
            });
        }

        if (state.keyword) {
            state.keyword.addEventListener('keypress', function (e) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    module.removePaginationInputs();
                    module.fetchResults(true);
                }
            });
        }

        if (state.clearBtn) {
            state.clearBtn.addEventListener('click', function () {
                state.form.reset();
                if (state.keyword) state.keyword.value = '';
                state.selects.forEach(select => { select.selectedIndex = 0; });
                window.history.pushState({}, '', state.ajaxUrl);
                module.fetchResults(true);
            });
        }

        window.addEventListener('popstate', function () {
            location.reload();
        });
    }

    /**
     * Scroll the .filtered-content-ajax section into view, offset by the
     * sticky header height measured from the <header> element at scroll time.
     *
     * Late-loading images/fonts above the section can shift its position after
     * the initial scroll has already started, leaving the user short of (or past)
     * the intended spot. To guard against that, we scroll immediately and then
     * re-measure/correct once more after the window has fully loaded.
     */
    function scrollToSection() {
        const section = document.querySelector(selectors.ajaxUrlSource);
        if (!section) return;

        const doScroll = () => {
            const header = document.querySelector('header');
            const headerHeight = header ? header.offsetHeight : 0;
            const top = section.getBoundingClientRect().top + window.scrollY - headerHeight - 30;
            window.scrollTo({ top, behavior: 'smooth' });
        };

        doScroll();

        if (document.readyState !== 'complete') {
            window.addEventListener('load', () => {
                // Double rAF ensures layout/paint from late-loading assets has settled
                // before we re-measure and correct the final scroll position.
                requestAnimationFrame(() => requestAnimationFrame(doScroll));
            }, { once: true });
        }
    }

    /**
     * Refresh the "Showing X of Y" counter element.
     */
    function updateCounter() {
        var counter = document.querySelector(selectors.counter);
        if (!counter) return;
        var container = document.querySelector(selectors.results);
        var total = container ? parseInt(container.dataset.total, 10) : 0;
        var shown = container ? container.querySelectorAll(':scope > li').length : 0;
        if (shown === 0) {
            counter.classList.add('hidden');
            return;
        }
        counter.classList.remove('hidden');
        counter.innerHTML = 'Showing <span id="results-counter-shown">' + shown + '</span> of <span id="results-counter-total">' + total + '</span> matching result' + (total === 1 ? '' : 's');
    }

    /**
     * Initialize the module: resolve DOM elements and bind events.
     * Returns false if required elements are missing.
     * @returns {boolean}
     */
    function init() {
        state.form    = document.querySelector(selectors.form);
        state.results = document.querySelector(selectors.results);
        state.overlay = document.querySelector(selectors.loadingOverlay);
        state.keyword = document.querySelector(selectors.keyword);
        state.clearBtn = document.querySelector(selectors.clearButton);
        state.selects = Array.from(document.querySelectorAll(selectors.filterSelects));
        state.ajaxUrl = module.resolveAjaxUrl();

        if (!state.form || !state.results || !state.overlay || !state.ajaxUrl) {
            return false;
        }

        module.bindEvents();

        // If the page was loaded with active filter params, scroll the section
        // into view so the user sees the pre-filtered results immediately.
        // Triggered once layout/paint has settled (rather than a fixed delay)
        // so the measured position is accurate even when assets load slowly.
        const urlParams = new URLSearchParams(window.location.search);
        if (filterParams.some(p => urlParams.has(p))) {
            const triggerScroll = () => requestAnimationFrame(() => requestAnimationFrame(() => module.scrollToSection()));
            if (document.readyState === 'complete') {
                triggerScroll();
            } else {
                window.addEventListener('load', triggerScroll, { once: true });
            }
        }

        return true;
    }

    // --- Assemble the public module ---
    // Merge defaults with any method overrides supplied via options.
    const module = Object.assign({
        state,
        init,
        bindEvents,
        fetchResults,
        getQueryString,
        removePaginationInputs,
        resolveAjaxUrl,
        renderError,
        showLoading,
        hideLoading,
        updateClearButton,
        updateCounter,
        scrollToSection,
    }, options);

    // Remove selectors from the public surface (it's only for internal use)
    delete module.selectors;

    return module;
}

// Default instance — auto-initializes on DOMContentLoaded.
// Skipped when another FilteringModule variant (e.g. load-more) is managing the page.
document.addEventListener('DOMContentLoaded', function () {
    if (!document.querySelector('.filtered-content-load-more') &&
        !document.querySelector('.filtered-content-ajax[data-variant]')) {
        FilteringModule().init();
    }
});
